import { Document, DocumentCreate, DocumentUpdate, PaginatedResponse } from '@types/index'
import apiClient from './client'

export const documentsAPI = {
  // Get all documents
  async getAll(params?: { skip?: number; limit?: number }): Promise<Document[]> {
    return apiClient.get('/api/documents', params)
  },

  // Get paginated documents
  async getPaginated(params: { 
    page?: number
    limit?: number
    sort?: string
    order?: 'asc' | 'desc'
    search?: string
  } = {}): Promise<PaginatedResponse<Document>> {
    const { page = 1, limit = 20, sort = 'updated_at', order = 'desc', search } = params
    const skip = (page - 1) * limit
    
    const queryParams: any = { skip, limit }
    if (search) queryParams.search = search
    if (sort) queryParams.sort = sort
    if (order) queryParams.order = order

    const documents = await apiClient.get('/api/documents', queryParams)
    
    // For now, return a simple paginated response
    // In the future, the backend should return proper pagination metadata
    return {
      items: documents,
      total: documents.length,
      page,
      limit,
      hasNext: documents.length === limit,
      hasPrev: page > 1,
    }
  },

  // Get document by ID
  async getById(id: string): Promise<Document> {
    return apiClient.get(`/api/documents/${id}`)
  },

  // Create new document
  async create(data: DocumentCreate): Promise<Document> {
    return apiClient.post('/api/documents', data)
  },

  // Update document
  async update(id: string, data: DocumentUpdate): Promise<Document> {
    return apiClient.put(`/api/documents/${id}`, data)
  },

  // Delete document
  async delete(id: string): Promise<void> {
    return apiClient.delete(`/api/documents/${id}`)
  },

  // Search documents
  async search(query: string, params?: { limit?: number }): Promise<Document[]> {
    return apiClient.get('/api/documents', { 
      search: query,
      limit: params?.limit || 50
    })
  },

  // Duplicate document
  async duplicate(id: string, title?: string): Promise<Document> {
    const original = await this.getById(id)
    return this.create({
      title: title || `${original.title} (Copy)`,
      content: original.content,
    })
  },

  // Get document statistics
  async getStatistics(id: string): Promise<{
    word_count: number
    character_count: number
    paragraph_count: number
    reading_time_minutes: number
  }> {
    const document = await this.getById(id)
    const content = document.content
    
    // Calculate statistics
    const words = content.trim().split(/\s+/).filter(word => word.length > 0)
    const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim().length > 0)
    const readingTime = Math.ceil(words.length / 200) // 200 WPM average

    return {
      word_count: words.length,
      character_count: content.length,
      paragraph_count: paragraphs.length,
      reading_time_minutes: readingTime,
    }
  },

  // Export document
  async export(id: string, format: 'pdf' | 'docx' | 'markdown' | 'txt'): Promise<void> {
    const document = await this.getById(id)
    const filename = `${document.title}.${format}`
    
    return apiClient.download(`/api/export/${format}`, filename)
  },

  // Batch operations
  async batchDelete(ids: string[]): Promise<void> {
    await Promise.all(ids.map(id => this.delete(id)))
  },

  async batchUpdate(updates: Array<{ id: string; data: DocumentUpdate }>): Promise<Document[]> {
    return Promise.all(updates.map(({ id, data }) => this.update(id, data)))
  },

  // Mark document as opened (for recent documents tracking)
  async markOpened(id: string): Promise<void> {
    return apiClient.post(`/api/documents/${id}/open`)
  },

  // Get recent documents
  async getRecent(limit: number = 10): Promise<Document[]> {
    return apiClient.get('/api/documents/recent', { limit })
  },

  // Get favorite documents
  async getFavorites(): Promise<Document[]> {
    return apiClient.get('/api/documents/favorites')
  },

  // Toggle document favorite status
  async toggleFavorite(id: string): Promise<Document> {
    return apiClient.post(`/api/documents/${id}/favorite`)
  },

  // Get authorship information (git blame)
  async getAuthorship(id: string, position?: number): Promise<{
    user_id?: string | null
    username?: string | null
    full_name?: string | null
    created_at?: string | null
    sentence_text?: string
    sentence_index?: number
    authorship?: Array<{
      user_id: string
      username: string
      full_name?: string
      sentence_index: number
      sentence_text: string
      start_position: number
      end_position: number
      created_at: string
    }>
  }> {
    const params = position !== undefined ? { position } : {}
    return apiClient.get(`/api/documents/${id}/authorship`, params)
  },

  // Get document statistics
  async getStatistics(): Promise<{
    total_documents: number
    words_today: number
    total_words: number
  }> {
    return apiClient.get('/api/documents/statistics')
  },
}